// To-dos — API docs §9. Daily recurring tasks assigned to an employee.
//
// The important idea: a to-do has NO done flag of its own. Ticking one writes a
// completion record stamped with the local date, so the list resets to pending
// at local midnight by itself and every past day stays queryable. `status` on
// the task is only ACTIVE (on the daily list) or PAUSED (hidden, history kept).
//
// Ticking is done by the employee in their own app — the partner panel has no
// endpoint to mark a task done. This panel manages the task definitions and
// reads each day's state.
//
// Scope: a BRANCH manages its own staff's tasks; a CHAIN manages tasks across
// all branches and narrows any read with `?partner=<branchId>`. The owning
// partner is derived from the assigned employee, so it is never sent in a body.
import api from "../lib/apiClient";

// §9.1 — `employee` or `employees` (max 200) is required; the response is an
// array with one document per assignee so each person's history is independent.
export const createTodo = (payload) => api.post("/todos", payload).then((r) => r.data);

// §9.2 — data is { items, pagination, date, summary }; each item already
// carries that day's isDone / completedAt / completionNote.
export const listTodos = (params = {}) => api.get("/todos", { params }).then((r) => r.data);

// §9.3 — the task, its state for `date`, and recentHistory (last 7 records).
export const getTodo = (id, params = {}) =>
  api.get(`/todos/${id}`, { params }).then((r) => r.data);

// §9.4 — title, description, priority, dueTime (null clears), order, status,
// employee (reassign). Past completions stay with whoever did the work.
export const updateTodo = (id, payload) =>
  api.patch(`/todos/${id}`, payload).then((r) => r.data);

// §9.5 — soft delete; completion records remain queryable in §9.7 / §9.8.
export const deleteTodo = (id) => api.delete(`/todos/${id}`).then((r) => r.data);

// §9.6 — exactly what one employee sees for a day:
// { employee, date, summary: { total, done, pending, completionRate }, items }.
export const getEmployeeDay = (employeeId, params = {}) =>
  api.get(`/todos/employees/${employeeId}`, { params }).then((r) => r.data);

// §9.7 — completion records, newest day first. from/to, employee, todo,
// partner, status (DONE | PENDING).
export const listTodoHistory = (params = {}) =>
  api.get("/todos/history", { params }).then((r) => r.data);

export const listOneTodoHistory = (id, params = {}) =>
  api.get(`/todos/${id}/history`, { params }).then((r) => r.data);

// §9.8 — { range, totals, byDate, byEmployee }. Defaults to the last 7 days,
// max range 92 days.
export const getTodoStats = (params = {}) =>
  api.get("/todos/stats", { params }).then((r) => r.data);
